import PropTypes from 'prop-types';
import { useRef } from 'react';

import { appTooltipProps } from '../../utils';

// Leaflet's bar buttons are 30 px wide: the slider takes the same column, three buttons tall
const WIDTH_PX = 30;
const HEIGHT_PX = 90;
const THUMB_WIDTH_PX = 18;
const THUMB_HEIGHT_PX = 6;
const SPAN_PX = HEIGHT_PX - (2 * THUMB_HEIGHT_PX);

//
// A vertical slider for an opacity, full at the top, hidden at the bottom, drawn as one entry of
// a Leaflet control bar: on a map it joins the zoom buttons (LeafletOpacityControl), elsewhere it
// sits in a `leaflet-bar` box of its own, so both read the same.
//
function LeafletOpacitySlider(props) {
  const {
    value,
    title,
    onChange,
  } = props;
  const draggingRef = useRef(false);

  // The thumb's centre runs from THUMB_HEIGHT_PX (full) to HEIGHT_PX - THUMB_HEIGHT_PX (hidden)
  const valueAt = (element, clientY) => {
    const { top } = element.getBoundingClientRect();
    const fromTop = Math.min(SPAN_PX, Math.max(0, clientY - top - THUMB_HEIGHT_PX));
    return 1 - (fromTop / SPAN_PX);
  };

  const onPointerDown = (event) => {
    event.preventDefault();
    draggingRef.current = true;
    event.currentTarget.setPointerCapture(event.pointerId);
    onChange(valueAt(event.currentTarget, event.clientY));
  };
  const onPointerMove = (event) => {
    if (draggingRef.current) onChange(valueAt(event.currentTarget, event.clientY));
  };
  const onPointerUp = (event) => {
    draggingRef.current = false;
    event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    // Leaflet's bar entries are anchors, which its stylesheet draws; the href only keeps that shape
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a
      href="#"
      role="slider"
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={value}
      {...appTooltipProps(title)}
      className="relative block bg-white cursor-pointer select-none touch-none"
      style={{
        width: WIDTH_PX,
        height: HEIGHT_PX,
      }}
      onClick={(event) => event.preventDefault()}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <div
        className="absolute bg-black/25"
        style={{
          left: (WIDTH_PX / 2) - 1,
          top: THUMB_HEIGHT_PX,
          width: 2,
          height: SPAN_PX,
        }}
      />
      <div
        className="absolute rounded-[1px] bg-black/75"
        style={{
          left: (WIDTH_PX - THUMB_WIDTH_PX) / 2,
          top: THUMB_HEIGHT_PX + ((1 - value) * SPAN_PX) - (THUMB_HEIGHT_PX / 2),
          width: THUMB_WIDTH_PX,
          height: THUMB_HEIGHT_PX,
        }}
      />
    </a>
  );
}

LeafletOpacitySlider.propTypes = {
  // 0 to 1
  value: PropTypes.number.isRequired,
  title: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

export default LeafletOpacitySlider;
